/**
 * Production verification shell: rail, top bar, content column.
 *
 * Extracted from excellent/client/prototypes/verification/app.tsx chrome for
 * WP1a (#46597). Pages are not imported here — WP1c relocates page families;
 * WP1b registers TanStack routes and mounts this shell around an outlet.
 *
 * Product-mode removals vs the prototype:
 *   - no Prototype flag
 *   - no fixture tenant switcher / Prototype toast
 *   - no local splat page resolver (HG12 route-per-page)
 *   - no isBareCanvasPath dependency; this shell is the product chrome
 *   - no visible sample/persisted-scope chrome
 *
 * #46742 — rail head is the org dropdown (search + add workflow). The
 * bottom-rail user chip is gone. Environment scope is supplied by EnvProvider;
 * its provenance must match what the provider actually read.
 */

import {
  Fragment,
  cloneElement,
  createContext,
  type FormEvent,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiCall, useApi } from "@excellent/ui/api";
import { useLiveResource } from "@excellent/ui/api/live";
import { activity } from "@excellent/ui/api/contracts/activity";
import { auth } from "@excellent/ui/api/contracts/auth";
import { shell } from "@excellent/ui/api/contracts/shell";
import {
  authenticationConnection,
  authenticationConnectionLabel,
  authenticationHref,
  observedReadiness,
  type AuthenticationConnection,
} from "../../../packages/ui/src/blocks/authentication-state";
import { CHART_TOOLTIP_STYLE } from "@excellent/ui/blocks/lens/chart-config";
import { advisor } from "@excellent/ui/api/contracts/advisor";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import {
  projectUniversalCompletionOverview,
  type UniversalConsoleOverview,
} from "@excellent/core/verification-universal-surfaces-client";
import { Trash2, X } from "@excellent/ui/icons";
import "./theme.css";
import "./companion-shell.css";
import type { AccountUser } from "./account-menu";
import { AgentMark, AgentMarkCluster } from "./agent-mark";
import { AskAboutObject } from "./ask-about";
import { objectKindFromChrome } from "./chat-entry";
import { CompanionActivity } from "./companion-activity";
import { CompanionSettings } from "./companion-settings";
import { StageOverlay } from "./stage-overlay";
import { TerminalDock } from "./terminal-dock";
import {
  activateTerminalInWindow,
  resolveTerminalAccess,
  TERMINAL_CONTEXT_REASON,
} from "./terminal-stage";
import { EnvProvider, useVerificationEnvOptional } from "./env-provider";
import { EnvIndicator } from "./env-indicator";
import {
  canonicalLocalEnvironmentAvailable,
  useCanonicalEnvironmentOptional,
} from "./canonical-environment-provider";
import { environmentContextExpired } from "@excellent/ui/blocks/excellent/environments/context-lifetime";
import { Icon, type IconName } from "./icons";
import { HomePage } from "./home-page";
import { openVerificationSearch, SearchButton, SearchPalette } from "./search-palette";
import { DockBar } from "../shell/chrome/dock-bar";
import { Logo } from "../shell/chrome/logo";
import { ClientObjectPeekHost } from "../shell/object-peek-host";
import { getShellData } from "../shell/shell-data";
import { ObjectInspectorPane } from "./object-inspector-pane";
import { WindowActions, WindowProjectSwitcher } from "./page-action-bar";
import {
  chromeRoute,
  groupFor,
  navItemFor,
  pathOf,
  preserveEnvironmentHref,
  type PageId,
} from "./nav";
import { installStaleAppRecovery } from "./stale-app-recovery";

installStaleAppRecovery();

function splitAppHref(to: string): { pathname: string; search: string; hash: string } {
  const hashIndex = to.indexOf("#");
  const withoutHash = hashIndex === -1 ? to : to.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : to.slice(hashIndex);
  const qIndex = withoutHash.indexOf("?");
  const pathname = (qIndex === -1 ? withoutHash : withoutHash.slice(0, qIndex)) || "/";
  const search = qIndex === -1 ? "" : withoutHash.slice(qIndex);
  return { pathname, search, hash };
}

type WorkSectionId = "agents" | "context" | "changes";

type WorkSection = {
  id: WorkSectionId;
  root: `/${string}`;
  title: string;
  icon: "cpu" | "book" | "chart";
  lede: string;
  items: readonly {
    href: string;
    label: string;
    description: string;
    icon:
      | "cpu"
      | "book"
      | "chart"
      | "plug"
      | "shield"
      | "file"
      | "seal"
      | "package"
      | "play"
      | "flag"
      | "inbox"
      | "link"
      | "check";
  }[];
};

const WORK_SECTIONS: Record<WorkSectionId, WorkSection> = {
  agents: {
    id: "agents",
    root: "/agents",
    title: "Agents",
    icon: "cpu",
    lede: "The tools, sessions, quality signals, and automation boundaries for agents doing work in this workspace.",
    items: [
      {
        href: "/settings/agents/clis",
        label: "Connected tools",
        description: "Review installed and authorized agent CLIs.",
        icon: "plug",
      },
      {
        href: "/agents/sessions",
        label: "Sessions",
        description: "See current and retained agent session trajectories.",
        icon: "link",
      },
      {
        href: "/agents/qa",
        label: "Quality",
        description: "Review agent checks, failures, and verified outcomes.",
        icon: "check",
      },
      {
        href: "/settings/agents/autopilot",
        label: "Automation",
        description: "Configure where agents may act automatically.",
        icon: "play",
      },
    ],
  },
  context: {
    id: "context",
    root: "/context",
    title: "Context",
    icon: "book",
    lede: "What done means, what was observed, and the policy and source records that make verification explainable.",
    items: [
      {
        href: "/controls",
        label: "Contracts",
        description: "Review accepted checks, predicates, evidence requirements, and bounds.",
        icon: "shield",
      },
      {
        href: "/evidence",
        label: "Evidence",
        description: "Inspect submitted artifacts, lineage, and integrity metadata.",
        icon: "book",
      },
      {
        href: "/policies",
        label: "Policies",
        description: "See the versioned rules that compile and govern contracts.",
        icon: "file",
      },
      {
        href: "/connectors",
        label: "Sources",
        description: "Review read-only connections, scope, and freshness.",
        icon: "plug",
      },
      {
        href: "/receipts",
        label: "Proof",
        description:
          "Inspect signed verdicts, replay state, receipts, and exportable evidence packs.",
        icon: "seal",
      },
    ],
  },
  changes: {
    id: "changes",
    root: "/changes",
    title: "Changes",
    icon: "chart",
    lede: "Work moving through execution, independent review, findings, and downstream outcomes.",
    items: [
      {
        href: "/runs",
        label: "Runs",
        description: "Inspect worker attempts and independent verification runs.",
        icon: "play",
      },
      {
        href: "/inbox",
        label: "Reviews",
        description: "Open work that needs an independent or named human decision.",
        icon: "inbox",
      },
      {
        href: "/findings",
        label: "Findings",
        description: "Review failed and unknown verification results.",
        icon: "flag",
      },
      {
        href: "/outcomes",
        label: "Outcomes",
        description: "Review regressions and receipt-backed follow-up.",
        icon: "check",
      },
    ],
  },
};

const SECTION_PATH_PREFIXES: Record<WorkSectionId, readonly string[]> = {
  agents: ["/agents", "/settings/agents"],
  context: [
    "/context",
    "/evidence",
    "/controls",
    "/policies",
    "/connectors",
    "/receipts",
    "/packs",
  ],
  changes: ["/changes", "/activity", "/runs", "/findings", "/inbox", "/work-ledger", "/outcomes"],
};

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function sectionForPath(pathname: string): WorkSection | null {
  for (const id of ["agents", "context", "changes"] as const) {
    if (SECTION_PATH_PREFIXES[id].some((prefix) => pathMatchesPrefix(pathname, prefix)))
      return WORK_SECTIONS[id];
  }
  return null;
}

function sectionForLocation(pathname: string, search: string): WorkSection | null {
  const from = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("from");
  if (from === "agents" || from === "context" || from === "changes") return WORK_SECTIONS[from];
  return sectionForPath(pathname);
}

function withSectionOrigin(to: string, section: WorkSection): string {
  if (!to.startsWith("/") || /^\/[\\/]/.test(to)) return to;
  const parts = splitAppHref(to);
  if (
    parts.pathname === "/" ||
    Object.values(WORK_SECTIONS).some((candidate) => candidate.root === parts.pathname)
  )
    return to;
  if (sectionForPath(parts.pathname)?.id === section.id) return to;
  const params = new URLSearchParams(
    parts.search.startsWith("?") ? parts.search.slice(1) : parts.search,
  );
  params.set("from", section.id);
  const query = params.toString();
  return `${parts.pathname}${query ? `?${query}` : ""}${parts.hash}`;
}

function withoutInspectorState(pathname: string, search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete("peek");
  params.delete("pane");
  params.delete("task");
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}

/**
 * Keep Overview chrome from sending a stranger into old ProductChrome.
 * The dedicated `/agents` section root stays in this shell. The retired
 * `/agents/improvement` doorway still returns to Home.
 * Does not unmount those routes — in-shell hops only.
 */
function stayOutOfProductChrome(to: string): string {
  let href = to;
  if (/^https?:/i.test(to)) {
    try {
      const url = new URL(to);
      href = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return to;
    }
  }
  const { pathname, search, hash } = splitAppHref(href);
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/agents/api" || normalized.startsWith("/agents/api/")) return href;
  if (normalized === "/settings/agents" || normalized.startsWith("/settings/agents/")) return href;
  if (normalized === "/agents/goals" || normalized.startsWith("/agents/goals/")) return href;
  if (normalized === "/agents/improvement") {
    return `/${search}${hash}`;
  }
  return href;
}

function productChromeHopHref(anchor: Element): string | null {
  const raw = anchor.getAttribute("href");
  if (!raw || raw.startsWith("#") || raw.startsWith("mailto:") || raw.startsWith("tel:"))
    return null;
  if (/^https?:/i.test(raw)) {
    try {
      const url = new URL(raw);
      if (typeof window !== "undefined" && url.origin !== window.location.origin) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }
  return raw;
}

export type VerificationShellProps = {
  children?: ReactNode;
  /** Live rail badges. Empty until WP4 contracts land. */
  counts?: Partial<Record<PageId, number>>;
  workspaceName?: string;
  workspaceInitials?: string;
  user?: AccountUser;
  /** Read-only runtime scope indicator. Omit to use the default chrome indicator. */
  environmentIndicator?: ReactNode | null;
};

export type VerificationTopBarState = {
  detailTitle?: string;
};

const VerificationTopBarContext = createContext<{
  state: VerificationTopBarState;
  setState: (state: VerificationTopBarState) => void;
} | null>(null);
const VerificationShellNestingContext = createContext(false);

export function useVerificationTopBar(state: VerificationTopBarState): void {
  const context = useContext(VerificationTopBarContext);
  useEffect(() => {
    if (!context) return;
    context.setState(state);
    return () => context.setState({});
  }, [state.detailTitle]);
}

const LOCAL_USER: AccountUser = {
  name: "Local companion",
  roleLabel: "Not signed in",
  initials: "LC",
};

export function VerificationShell({ children, ...props }: VerificationShellProps) {
  const nested = useContext(VerificationShellNestingContext);
  if (nested) return <>{children}</>;
  return <VerificationShellRoot {...props}>{children}</VerificationShellRoot>;
}

function VerificationShellRoot({
  children,
  counts,
  workspaceName: _workspaceName = "Workspace",
  workspaceInitials: _workspaceInitials = "W",
  user: providedUser,
  environmentIndicator,
}: VerificationShellProps) {
  useLightCanvas();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const pathname = location.pathname;
  const search = location.searchStr ?? "";
  const route = chromeRoute(pathname);
  const homeIsRoot = pathname === "/";
  const section = sectionForLocation(pathname, search);
  const sectionIsRoot = section?.root === pathname;
  const activeSectionItem = section?.items.find((item) =>
    pathMatchesPrefix(pathname, splitAppHref(item.href).pathname),
  );
  const inspectorUid =
    new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("peek")?.trim() ||
    null;
  const sectionDetailOpen = Boolean(section && (!sectionIsRoot || inspectorUid));
  const shellData = getShellData();
  const authState = useApi(auth.me, {});
  const shellObservation = useApi(shell.get, {}, { refreshInterval: 30_000 });
  const [networkAvailable, setNetworkAvailable] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );
  useEffect(() => {
    const offline = () => setNetworkAvailable(false);
    const online = () => {
      // SWR suspends polling offline. Re-observe both boundaries on reconnect;
      // browser connectivity alone does not establish workspace access.
      void authState.mutate();
      void shellObservation.mutate();
      setNetworkAvailable(true);
    };
    window.addEventListener("offline", offline);
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("offline", offline);
      window.removeEventListener("online", online);
    };
  }, [authState.mutate, shellObservation.mutate]);
  const connection = authenticationConnection(authState, shellObservation, networkAvailable);
  const authenticated =
    !authState.error && connection !== "sign-in" && authState.data?.authenticated === true;
  const destination = `${pathname}${search}${typeof window === "undefined" ? "" : window.location.hash}`;
  const authenticationTarget = destination === "/" ? "/login" : authenticationHref(destination);
  useEffect(() => {
    if (connection !== "sign-in" || typeof window === "undefined") return;
    window.location.assign(authenticationTarget);
  }, [authenticationTarget, connection]);
  const user =
    providedUser ??
    (authenticated && authState.data?.handle
      ? {
          name: authState.data.handle,
          roleLabel: "Signed in",
          initials: authState.data.handle.slice(0, 2).toUpperCase(),
        }
      : LOCAL_USER);
  const copilotEnabled = shellData.copilot.enabled;
  // The actual terminal decision is made below the canonical Environment
  // provider. Keep this fallback locked until that provider has proved a local
  // target; browser hostname is not locality evidence.
  const terminalAccess = resolveTerminalAccess({ roleLabel: user.roleLabel, locality: "unknown" });
  const [railOpen, setRailOpen] = useState(false);
  const [mobilePane, setMobilePane] = useState<"work" | "coordinator" | "details">("details");
  const [topBarState, setTopBarState] = useState<VerificationTopBarState>({});
  const [inspectorTitle, setInspectorTitle] = useState<string | null>(null);
  const topBarContext = useMemo(
    () => ({ state: topBarState, setState: setTopBarState }),
    [topBarState],
  );

  const go = useCallback(
    (to: string) => {
      if (to === "/login") {
        window.location.assign(authenticationHref(destination));
        return;
      }
      setRailOpen(false);
      const scoped = preserveEnvironmentHref(stayOutOfProductChrome(to), search);
      const dest = section ? withSectionOrigin(scoped, section) : scoped;
      if (section && dest !== section.root) setMobilePane("details");
      void navigate({ to: dest, resetScroll: true });
    },
    [navigate, search, section, destination],
  );

  const closeInspector = useCallback(() => {
    setInspectorTitle(null);
    void navigate({
      to: withoutInspectorState(pathname, search),
      replace: true,
      resetScroll: false,
    });
  }, [navigate, pathname, search]);

  const onProductChromeHop = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!anchor) return;
      const href = productChromeHopHref(anchor);
      if (!href) return;
      const normalized = preserveEnvironmentHref(stayOutOfProductChrome(href), search);
      const destination = section ? withSectionOrigin(normalized, section) : normalized;
      if (destination === href) return;
      event.preventDefault();
      event.stopPropagation();
      go(href);
    },
    [go, search, section],
  );

  const onLogout = () => {
    void (async () => {
      try {
        await apiCall(auth.logout, {});
      } finally {
        window.location.assign("/");
      }
    })();
  };

  useEffect(() => {
    document.querySelector(".vp-scroll")?.scrollTo({ top: 0 });
  }, [route.page, route.id]);

  useEffect(() => {
    if (homeIsRoot) {
      setMobilePane(inspectorUid ? "details" : "coordinator");
      return;
    }
    if (!section) return;
    setMobilePane(sectionDetailOpen ? "details" : "coordinator");
  }, [homeIsRoot, inspectorUid, section?.id, sectionDetailOpen]);

  // The operational shell is an authenticated surface. Keep the auth and
  // connection states outside it so a visitor never sees workspace data,
  // navigation, or agent controls behind a sign-in banner.
  if (connection !== "ready") {
    return (
      <AuthenticationBoundary
        state={connection}
        destination={destination}
        authenticationTarget={authenticationTarget}
        retrying={authState.isValidating || shellObservation.isValidating}
        onRetry={() => {
          void authState.mutate();
          void shellObservation.mutate();
        }}
      />
    );
  }

  return (
    <VerificationShellNestingContext.Provider value={true}>
      <EnvProvider
        key={JSON.stringify([
          authState.data?.userId,
          authState.data?.workspaceId,
          shellObservation.data?.workspaceId,
        ])}
        canonicalIdentity={{
          authenticated,
          userId: authState.data?.userId ?? null,
          handle: authState.data?.handle ?? null,
          workspaceId: authState.data?.workspaceId ?? null,
          shellWorkspaceId: shellObservation.data?.workspaceId ?? null,
        }}
        pathname={pathname}
        search={search}
        workspaceId={shellData.workspaceId == null ? null : String(shellData.workspaceId)}
        cacheNamespace={`account:${authState.data?.handle ?? "unknown"}:workspace:${shellData.workspaceId ?? "none"}`}
        onNavigate={go}
        onNavigateWithoutEnvironment={(to) => void navigate({ to, resetScroll: true })}
      >
        <VerificationTopBarContext.Provider value={topBarContext}>
          <EnvScopedShellState>
            <div
              className="vp"
              data-verification-shell="product"
              data-verification-env=""
              data-mobile-pane={mobilePane}
              data-home-root={homeIsRoot ? "true" : undefined}
              data-work-section={section?.id}
              data-section-detail-open={sectionDetailOpen ? "true" : undefined}
              data-inspector-open={inspectorUid ? "true" : undefined}
              onClickCapture={onProductChromeHop}
            >
              <a
                className="vp-skip"
                href="#vp-content"
                onClick={() =>
                  setMobilePane(
                    inspectorUid || (!homeIsRoot && !sectionIsRoot) ? "details" : "coordinator",
                  )
                }
              >
                Skip to details
              </a>
              <WindowStrip
                pathname={pathname}
                search={search}
                projects={shellData.projects}
                allProjects={shellData.allProjects}
                defaultSlug={shellData.defaultSlug}
                user={user}
                authenticated={authenticated}
                connection={connection}
                onNavigate={go}
                workspaces={shellData.workspaces}
                activeWorkspaceId={shellData.workspaceId}
                copilotEnabled={copilotEnabled}
                terminalAccess={terminalAccess}
                terminalRoleLabel={user.roleLabel}
                onLogout={onLogout}
                environmentIndicator={
                  environmentIndicator === undefined ? (
                    <EnvIndicator onNavigate={go} />
                  ) : (
                    environmentIndicator
                  )
                }
              />
              <div className="vp-shell-body">
                <Rail
                  connection={connection}
                  current={route.page}
                  detailId={route.id}
                  detailTitle={topBarState.detailTitle}
                  pathname={pathname}
                  onNavigate={go}
                  open={railOpen}
                  counts={counts}
                  terminalAccess={terminalAccess}
                  terminalRoleLabel={user.roleLabel}
                  workspaces={shellData.workspaces}
                  activeWorkspaceId={shellData.workspaceId}
                  section={section?.id ?? null}
                  environmentIndicator={null}
                />
                {railOpen ? (
                  <button
                    type="button"
                    className="vp-rail-scrim"
                    aria-label="Close navigation"
                    data-action-id="shell.close-navigation"
                    onClick={() => setRailOpen(false)}
                  />
                ) : null}
                {inspectorUid ? null : homeIsRoot ? (
                  <HomeRootPane onNavigate={go} />
                ) : section ? (
                  sectionDetailOpen ? null : (
                    <SectionHomePane
                      connection={connection}
                      section={section}
                      pathname={pathname}
                      onNavigate={go}
                      onClose={() => go("/")}
                    />
                  )
                ) : (
                  <CompanionPane
                    route={route}
                    pathname={pathname}
                    connection={connection}
                    onRetryConnection={() =>
                      Promise.all([authState.mutate(), shellObservation.mutate()])
                    }
                    copilotEnabled={copilotEnabled}
                    onNavigate={go}
                  />
                )}
                {inspectorUid || (!homeIsRoot && (!section || sectionDetailOpen)) ? (
                  <section
                    className="vp-workspace"
                    id={inspectorUid ? "vp-content" : undefined}
                    tabIndex={inspectorUid ? -1 : undefined}
                    aria-labelledby={
                      inspectorUid ? "vp-inspector-document-title" : "vp-detail-pane-title"
                    }
                    data-pane={inspectorUid ? "inspector" : "detail"}
                  >
                    {inspectorUid ? (
                      <>
                        <h1 id="vp-inspector-document-title" className="vp-sr-only">
                          {inspectorTitle ?? "Details"}
                        </h1>
                        <ObjectInspectorPane
                          uid={inspectorUid}
                          onClose={closeInspector}
                          onNavigate={go}
                          focusOnMount
                          onTitleChange={setInspectorTitle}
                          fallbackTitle={inspectorTitle ?? "Details"}
                        />
                      </>
                    ) : (
                      <div className="vp-main">
                        <TopBar
                          route={route}
                          onNavigate={go}
                          onClose={section ? () => go(section.root) : undefined}
                          titleOverride={activeSectionItem?.label}
                          railOpen={railOpen}
                          onToggleRail={() => {
                            setMobilePane("work");
                            setRailOpen((o) => !o);
                          }}
                          copilotEnabled={copilotEnabled}
                          pathname={pathname}
                          topBarState={topBarState}
                        />
                        <div className="vp-scroll" id="vp-content" tabIndex={-1}>
                          {pathname === "/settings" ? (
                            <CompanionSettings onNavigate={go} />
                          ) : pathname === "/activity" ? (
                            <CompanionActivity onNavigate={go} search={search} />
                          ) : (
                            children
                          )}
                        </div>
                        <EnvironmentTerminalDock
                          roleLabel={user.roleLabel}
                          fallback={terminalAccess}
                        />
                      </div>
                    )}
                  </section>
                ) : null}
              </div>
              <MobilePaneNav
                active={mobilePane}
                onSelect={setMobilePane}
                section={section}
                homeRoot={homeIsRoot}
                detailAvailable={Boolean(
                  inspectorUid || (!homeIsRoot && (!section || sectionDetailOpen)),
                )}
              />
              <SearchPalette onNavigate={go} />
              {inspectorUid ? null : <ClientObjectPeekHost />}
              <div data-verification-chat-dock="">
                <DockBar
                  composer="hidden"
                  copilotEnabled={copilotEnabled}
                  copilotDevelop={shellData.copilot.develop}
                  pages={[]}
                />
              </div>
            </div>
          </EnvScopedShellState>
        </VerificationTopBarContext.Provider>
      </EnvProvider>
    </VerificationShellNestingContext.Provider>
  );
}

function AuthenticationBoundary({
  state,
  destination,
  authenticationTarget,
  onRetry,
  retrying,
}: {
  state: AuthenticationConnection;
  destination: string;
  authenticationTarget: string;
  onRetry: () => void;
  retrying: boolean;
}) {
  const isSignIn = state === "sign-in";
  const isChecking = state === "checking";
  if (isChecking) return <AuthenticationLoadingSkeleton />;
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-background px-6 py-12 text-foreground"
      data-authentication-gate="true"
      data-authentication-state={state}
    >
      <section className="w-full max-w-xl rounded-2xl border border-border bg-card p-8 shadow-sm sm:p-10">
        <div className="flex items-center gap-3">
          <Logo size={28} />
          <span className="text-sm font-medium tracking-wide text-muted-foreground">Excellent</span>
        </div>
        {isSignIn ? (
          <>
            <p className="mt-10 text-sm font-medium uppercase tracking-[0.16em] text-primary">
              Private workspace
            </p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight">
              Sign in to enter Excellent
            </h1>
            <p className="mt-3 max-w-lg text-muted-foreground">
              Your work, evidence, and signed verdicts are local to your authenticated workspace.
              Sign in with your passkey to continue
              {destination === "/" ? "." : " to the page you requested."}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                className="inline-flex min-h-10 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                href={authenticationTarget}
              >
                Continue to sign in
              </a>
              <span className="text-sm text-muted-foreground">Redirecting…</span>
            </div>
          </>
        ) : (
          <>
            <h1 className="mt-10 text-3xl font-semibold tracking-tight">
              Workspace access unavailable
            </h1>
            <p className="mt-3 text-muted-foreground">
              Excellent could not confirm the local workspace connection. Your data stays protected
              while the connection is unavailable.
            </p>
            <button
              type="button"
              className="mt-8 inline-flex min-h-10 items-center justify-center rounded-md border border-border bg-background px-5 text-sm font-medium hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-60"
              onClick={onRetry}
              disabled={retrying}
            >
              {retrying ? "Checking connection…" : "Retry connection"}
            </button>
          </>
        )}
      </section>
    </main>
  );
}

function AuthenticationLoadingSkeleton() {
  return (
    <main
      className="flex min-h-screen flex-col bg-background text-foreground"
      data-authentication-gate="true"
      data-authentication-state="checking"
      role="status"
      aria-busy="true"
      aria-label="Loading Excellent"
    >
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-5 sm:px-6">
        <div className="flex items-center gap-3">
          <Logo size={24} />
          <span className="text-sm font-medium tracking-wide text-muted-foreground">Excellent</span>
        </div>
        <div
          className="h-2.5 w-20 animate-pulse rounded-full bg-accent motion-reduce:animate-none"
          aria-hidden="true"
        />
      </header>
      <div className="grid min-h-0 flex-1 md:grid-cols-[72px_minmax(0,1fr)]">
        <aside
          className="hidden flex-col items-center gap-5 border-r border-border pt-8 md:flex"
          aria-hidden="true"
        >
          {["size-5", "size-5", "size-5", "size-5", "size-4"].map((size, index) => (
            <div
              className={`${size} animate-pulse rounded-md bg-accent motion-reduce:animate-none`}
              key={index}
            />
          ))}
        </aside>
        <section className="min-w-0 overflow-hidden p-6 sm:p-8 lg:p-12" aria-hidden="true">
          <div className="mx-auto max-w-5xl">
            <div className="h-3 w-20 animate-pulse rounded bg-accent motion-reduce:animate-none" />
            <div className="mt-4 h-8 w-48 max-w-full animate-pulse rounded bg-accent motion-reduce:animate-none" />
            <div className="mt-3 h-4 w-64 max-w-full animate-pulse rounded bg-accent motion-reduce:animate-none" />
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {["w-full", "w-5/6", "w-2/3"].map((width, index) => (
                <div className="rounded-lg border border-border p-5" key={index}>
                  <div
                    className={`${width} h-4 animate-pulse rounded bg-accent motion-reduce:animate-none`}
                  />
                  <div className="mt-5 h-20 animate-pulse rounded bg-accent motion-reduce:animate-none" />
                </div>
              ))}
            </div>
            <div className="mt-8 space-y-3 rounded-lg border border-border p-5">
              {["w-full", "w-5/6", "w-2/3"].map((width, index) => (
                <div
                  className={`${width} h-4 animate-pulse rounded bg-accent motion-reduce:animate-none`}
                  key={index}
                />
              ))}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function shellContext(
  pathname: string,
  route: { page: PageId; id?: string },
  detailTitle?: string,
): { label: string; purpose: string; group: string } {
  if (pathname === "/") {
    return {
      label: "Home",
      purpose: "What Excellent recognizes, what needs attention, and the next useful check.",
      group: "Home",
    };
  }
  if (pathname === "/activity" || pathname.startsWith("/activity/")) {
    return {
      label: "Activity",
      purpose:
        "Meaningful observations, checks, findings, decisions, and receipts across this workspace.",
      group: "Activity",
    };
  }
  if (pathname !== "/" && route.page === "overview" && !route.id) {
    const firstSegment = pathname.split("/").filter(Boolean)[0];
    const label = firstSegment ? readableDetailLabel(firstSegment) : "Home";
    return {
      label,
      purpose: "The selected Excellent surface and its authoritative detail.",
      group: "Companion",
    };
  }
  const item = navItemFor(route.page);
  if (route.id) {
    return {
      label: detailTitle ?? readableDetailLabel(route.id),
      purpose: item?.purpose ?? "The selected Excellent object and its authoritative detail.",
      group: item ? (groupFor(route.page) ?? item.label) : "Detail",
    };
  }
  const section = sectionForPath(pathname);
  if (section) {
    const sectionItem = section.items.find((candidate) =>
      pathMatchesPrefix(pathname, splitAppHref(candidate.href).pathname),
    );
    return {
      label: sectionItem?.label ?? section.title,
      purpose: sectionItem?.description ?? section.lede,
      group: section.title,
    };
  }
  if (item) {
    return {
      label: item.label,
      purpose: item.purpose,
      group: groupFor(route.page) ?? item.label,
    };
  }
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  const label = firstSegment ? readableDetailLabel(firstSegment) : "Home";
  return {
    label,
    purpose: "The selected Excellent surface and its authoritative detail.",
    group: "Companion",
  };
}

function workTitleFor(
  pathname: string,
  route: { page: PageId; id?: string },
  detailTitle?: string,
  section?: WorkSectionId | null,
): string {
  if (section) return WORK_SECTIONS[section].title;
  return shellContext(pathname, route, detailTitle).label;
}

function WindowStrip({
  pathname,
  search,
  projects,
  allProjects,
  defaultSlug,
  user,
  authenticated,
  connection,
  onNavigate,
  workspaces,
  activeWorkspaceId,
  copilotEnabled,
  terminalAccess,
  terminalRoleLabel,
  onLogout,
  environmentIndicator,
}: {
  pathname: string;
  search: string;
  projects: ReturnType<typeof getShellData>["projects"];
  allProjects: ReturnType<typeof getShellData>["allProjects"];
  defaultSlug: string | null;
  user: AccountUser;
  authenticated: boolean;
  connection: AuthenticationConnection;
  onNavigate: (to: string) => void;
  workspaces: ReturnType<typeof getShellData>["workspaces"];
  activeWorkspaceId: number | null;
  copilotEnabled: boolean;
  terminalAccess: ReturnType<typeof resolveTerminalAccess>;
  terminalRoleLabel: string;
  onLogout: () => void;
  environmentIndicator: ReactNode;
}) {
  const effectiveTerminalAccess = useEnvironmentTerminalAccess(terminalAccess, terminalRoleLabel);
  return (
    <nav className="vp-window-strip" aria-label="Application">
      <button
        className="vp-window-mark"
        type="button"
        data-action-id="shell.open-home"
        onClick={() => onNavigate("/")}
        aria-label="Excellent home"
      >
        <Logo size={20} />
      </button>
      <WindowProjectSwitcher
        pathname={pathname}
        search={search}
        projects={projects}
        allProjects={allProjects}
        defaultSlug={defaultSlug}
        onNavigate={onNavigate}
      />
      <span className="vp-window-spacer" />
      {environmentIndicator}
      <span className="vp-node-state" role="status" data-connection-state={connection}>
        {connection === "ready" ? <span className="vp-live-dot" aria-hidden="true" /> : null}
        {authenticationConnectionLabel[connection]}
      </span>
      <WindowActions
        pathname={pathname}
        user={user}
        authenticated={authenticated}
        onNavigate={onNavigate}
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspaceId}
        copilotEnabled={copilotEnabled}
        terminalAccess={effectiveTerminalAccess}
        onLogout={onLogout}
      />
    </nav>
  );
}

function useEnvironmentTerminalAccess(
  fallback: ReturnType<typeof resolveTerminalAccess>,
  roleLabel: string,
): ReturnType<typeof resolveTerminalAccess> {
  const context = useCanonicalEnvironmentOptional();
  if (!context) return fallback;
  const response = context.response;
  if (
    context.error ||
    context.invalidRoute ||
    !response ||
    !["ready", "location-unknown"].includes(response.status) ||
    !response.presentation ||
    environmentContextExpired(response)
  ) {
    return { allowed: false, reason: TERMINAL_CONTEXT_REASON };
  }
  if (
    response.operationalStatus.state !== "current" ||
    response.operationalStatus.data.capabilities.capabilities.shell === "none"
  ) {
    return { allowed: false, reason: TERMINAL_CONTEXT_REASON };
  }
  return resolveTerminalAccess({
    roleLabel,
    locality: response.presentation.locality.kind,
  });
}

function EnvironmentTerminalDock({
  roleLabel,
  fallback,
}: {
  roleLabel: string;
  fallback: ReturnType<typeof resolveTerminalAccess>;
}) {
  const access = useEnvironmentTerminalAccess(fallback, roleLabel);
  return (
    <>
      <StageOverlay access={access} />
      <TerminalDock access={access} />
    </>
  );
}

/**
 * Force the document into light mode while the verification shell is mounted.
 * Restoring on unmount matters: WP11a keeps the old product reachable, so
 * navigating away must not leave that surface in the wrong theme.
 */
function useLightCanvas() {
  useEffect(() => {
    const root = document.documentElement;
    const had = {
      dark: root.classList.contains("dark"),
      indigo: root.classList.contains("indigo-dark"),
    };
    root.classList.remove("dark", "indigo-dark");
    const priorScheme = root.style.colorScheme;
    root.style.colorScheme = "light";
    return () => {
      if (had.dark) root.classList.add("dark");
      if (had.indigo) root.classList.add("indigo-dark");
      root.style.colorScheme = priorScheme;
    };
  }, []);
}

function EnvScopedShellState({ children }: { children: ReactNode }): ReactNode {
  const env = useVerificationEnvOptional();
  const key = env?.environment ?? "real";
  const envAttrs = {
    "data-verification-env": env?.environment ?? "",
    "data-verification-env-source": env?.source ?? "",
  };
  if (isValidElement(children)) {
    return cloneElement(children, { key, ...envAttrs });
  }
  return <Fragment key={key}>{children}</Fragment>;
}

function Rail({
  connection,
  current,
  detailId,
  detailTitle,
  pathname,
  onNavigate,
  open,
  counts,
  terminalAccess,
  terminalRoleLabel,
  workspaces,
  activeWorkspaceId,
  section,
  environmentIndicator,
}: {
  connection: AuthenticationConnection;
  current: PageId;
  detailId?: string;
  detailTitle?: string;
  pathname: string;
  onNavigate: (to: string) => void;
  open: boolean;
  counts?: Partial<Record<PageId, number>>;
  terminalAccess: ReturnType<typeof resolveTerminalAccess>;
  terminalRoleLabel: string;
  workspaces: ReturnType<typeof getShellData>["workspaces"];
  activeWorkspaceId: number | null;
  section: WorkSectionId | null;
  environmentIndicator: ReactNode | null;
}) {
  const effectiveTerminalAccess = useEnvironmentTerminalAccess(terminalAccess, terminalRoleLabel);
  const context = shellContext(pathname, { page: current, id: detailId }, detailTitle);
  const workTitle = workTitleFor(pathname, { page: current, id: detailId }, detailTitle, section);
  const workspaceName =
    workspaces.find((workspace) => workspace.id === activeWorkspaceId)?.name ?? "Excellent";
  const agentReadiness = useApi(advisor.agentCliReadiness, {});
  const readyAgents = observedReadiness(agentReadiness, connection);
  const installedClis = readyAgents?.filter((cli) => cli.binary.installed) ?? [];
  const installedAgents = readyAgents ? installedClis.length : "—";
  const verificationEnv = useVerificationEnvOptional();
  const canonicalEnvironment = useCanonicalEnvironmentOptional();
  const localEnvironmentReady = canonicalLocalEnvironmentAvailable(canonicalEnvironment);
  const universalOverview = useApi(
    universalVerification.overview,
    verificationEnv?.environment === "demo" || !localEnvironmentReady
      ? null
      : { query: { env: "real" } },
  );
  const activityFeed = useLiveResource(
    activity.list,
    {},
    { topic: "activity", refreshInterval: 30_000 },
  );
  const groups =
    connection === "ready" && !activityFeed.error ? (activityFeed.data?.groups ?? []) : [];
  const activityRows = groups
    .flatMap((group) => group.members.map((member) => ({ group, member })))
    .sort((a, b) => b.member.occurred_at.localeCompare(a.member.occurred_at))
    .slice(0, 5);
  const evidenceCount = counts?.evidence ?? 0;
  const universalData = universalOverview.data;
  const completionProjection = useMemo(
    () => (universalData ? projectUniversalCompletionOverview(universalData) : null),
    [universalData],
  );
  const currentUniversalData = completionProjection?.current;
  const incompleteRecordCount = completionProjection
    ? Object.values(completionProjection.incomplete).reduce(
        (sum, records) => sum + records.length,
        0,
      )
    : 0;
  const prospectiveReceipts =
    currentUniversalData?.receipts.filter(
      (receipt) => receipt.payload.assurance === "PROSPECTIVE",
    ) ?? [];
  const retrospectiveReceipts =
    currentUniversalData?.receipts.filter(
      (receipt) => receipt.payload.assurance === "RETROSPECTIVE",
    ) ?? [];
  const workCount = currentUniversalData?.work.length ?? 0;
  const workWithVerdict = new Set(
    prospectiveReceipts.map((receipt) => receipt.payload.binding.workItemId),
  ).size;
  const verdictCoverage = workCount > 0 ? Math.round((workWithVerdict / workCount) * 100) : 0;
  const openWorkCount =
    currentUniversalData?.work.filter((work) => work.state !== "CLOSED").length ?? 0;
  const contextCount = currentUniversalData?.contracts.length ?? evidenceCount;
  const universalUnavailable =
    verificationEnv?.environment === "demo" ||
    !localEnvironmentReady ||
    Boolean(universalOverview.error);
  const universalPending =
    verificationEnv?.environment !== "demo" &&
    localEnvironmentReady &&
    universalOverview.isLoading &&
    !universalData;

  return (
    <aside
      id="vp-context-navigation"
      className="vp-rail vp-work-pane"
      aria-labelledby="vp-work-title"
      data-open={open ? "true" : undefined}
    >
      <header className="vp-work-head">
        <div className="vp-work-heading">
          <h2 id="vp-work-title" className="vp-section-home-title">
            {workTitle}
          </h2>
          <p>
            {workspaceName} <span aria-hidden="true">·</span> {context.group}
          </p>
        </div>
      </header>

      <section
        className="vp-work-summary"
        aria-label="Verification progress"
        data-completion-current-work={workCount}
        data-completion-current-receipts={prospectiveReceipts.length}
        data-completion-historical={incompleteRecordCount}
        data-completion-retrospective={retrospectiveReceipts.length}
      >
        <div className="vp-work-progress-label">
          <span>Work with a verdict</span>
          <strong>
            {universalPending
              ? "Loading"
              : universalUnavailable
                ? "Unavailable"
                : workCount > 0
                  ? `${workWithVerdict} of ${workCount}`
                  : "No work"}
          </strong>
        </div>
        {workCount > 0 && !universalUnavailable ? (
          <div
            className="vp-work-progress"
            role="progressbar"
            aria-label="Work items with a signed verdict"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={verdictCoverage}
          >
            <span style={{ width: `${verdictCoverage}%` }} />
          </div>
        ) : (
          <div className="vp-work-progress" data-empty="true" aria-hidden="true">
            <span />
          </div>
        )}
        <p>{context.purpose}</p>
        {incompleteRecordCount > 0 ? (
          <p data-completion-history="true">
            {incompleteRecordCount} historical records remain readable as INCOMPLETE or STALE.
            Recorded statuses are facts only and do not authorize counts, charts, PASS/ALLOW, or
            enforcement.
          </p>
        ) : null}
        {retrospectiveReceipts.length > 0 ? (
          <p data-completion-retrospective-note="true">
            {retrospectiveReceipts.length} inline retrospective receipt
            {retrospectiveReceipts.length === 1 ? " is" : "s are"} recorded and visible, but cannot
            authorize PASS/ALLOW or enforcement.
          </p>
        ) : null}
      </section>

      <section className="vp-work-activity" aria-label="Recent activity">
        {groups.length > 0 ? (
          <ol className="vp-work-events">
            {activityRows.map(({ group, member }) => (
              <li key={member.id}>
                <button
                  type="button"
                  data-action-id="shell.open-activity-event"
                  onClick={() => onNavigate(member.href || group.href || "/activity")}
                >
                  <span className="vp-work-event-mark">
                    <WorkActivityMark groupKey={group.key} />
                  </span>
                  <span className="vp-work-event-label">{member.label ?? group.label}</span>
                  <time dateTime={member.occurred_at}>
                    {relativeActivityTime(member.occurred_at)}
                  </time>
                </button>
              </li>
            ))}
          </ol>
        ) : installedClis.length > 0 ? (
          <ol className="vp-work-events">
            {installedClis.slice(0, 5).map((cli) => (
              <li key={cli.id}>
                <button
                  type="button"
                  data-action-id="shell.manage-agent-clis"
                  onClick={() => onNavigate("/settings/agents/clis")}
                >
                  <span className="vp-work-event-mark">
                    <AgentMark agentKey={cli.id} size={14} />
                  </span>
                  <span className="vp-work-event-label">{cli.label} observed</span>
                  <span className="vp-work-event-state">Current</span>
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <div className="vp-work-empty">
            {connection === "ready" && !activityFeed.error && activityFeed.data ? (
              <span className="vp-live-dot" aria-hidden="true" />
            ) : null}
            <span>
              {connection === "sign-in"
                ? "Sign in to load workspace activity."
                : connection === "unavailable" || activityFeed.error
                  ? "Activity could not be loaded."
                  : connection === "ready" && activityFeed.data
                    ? "No meaningful changes yet."
                    : "Listening for workspace changes."}
            </span>
          </div>
        )}
        <button
          className="vp-work-more"
          type="button"
          data-action-id="shell.show-more-activity"
          onClick={() => onNavigate("/activity")}
        >
          <span className="vp-work-event-mark">
            <Icon name="arrow-right" size={14} />
          </span>
          <span className="vp-work-event-label">Show more</span>
        </button>
      </section>

      <nav className="vp-tool-grid" aria-label="Work tools">
        <button
          type="button"
          data-action-id="shell.open-agents"
          data-active={section === "agents" ? "true" : undefined}
          aria-pressed={section === "agents"}
          onClick={() => onNavigate("/agents")}
        >
          <AgentMarkCluster agentKeys={installedClis.map((cli) => cli.id)} size={16} />
          <span>Agents</span>
          <small>{installedAgents || ""}</small>
        </button>
        <button
          type="button"
          data-action-id="shell.open-context"
          data-active={section === "context" ? "true" : undefined}
          aria-pressed={section === "context"}
          onClick={() => onNavigate("/context")}
        >
          <Icon name="book" size={16} />
          <span>Context</span>
          <small>{contextCount || ""}</small>
        </button>
        <button
          type="button"
          data-action-id="shell.open-changes"
          data-active={section === "changes" ? "true" : undefined}
          aria-pressed={section === "changes"}
          onClick={() => onNavigate("/changes")}
        >
          <Icon name="chart" size={16} />
          <span>Changes</span>
          <small>{openWorkCount ? `${openWorkCount} not closed` : ""}</small>
        </button>
        <button
          type="button"
          data-action-id="shell.search-files"
          onClick={() => openVerificationSearch()}
        >
          <Icon name="file" size={16} />
          <span>Files</span>
          <small>Search</small>
        </button>
        <button type="button" disabled title="Browser stage is not available on this node">
          <Icon name="external" size={16} />
          <span>Browser</span>
          <small>Unavailable</small>
        </button>
        <button
          type="button"
          disabled={!effectiveTerminalAccess.allowed}
          title={effectiveTerminalAccess.allowed ? "Open shell" : effectiveTerminalAccess.reason}
          onClick={() => activateTerminalInWindow(effectiveTerminalAccess)}
        >
          <Icon name="code" size={16} />
          <span>Shell</span>
          <small>{effectiveTerminalAccess.allowed ? "Open" : "Locked"}</small>
        </button>
      </nav>
      {environmentIndicator ? <div className="vp-rail-foot">{environmentIndicator}</div> : null}
    </aside>
  );
}

type SectionMetric = {
  label: string;
  value: number | string;
  note: string;
};

function sectionMetrics(
  section: WorkSectionId,
  data: UniversalConsoleOverview | undefined,
  installedTools: number | undefined,
  linkedTools: number | undefined,
): SectionMetric[] {
  const current = data ? projectUniversalCompletionOverview(data).current : undefined;
  if (section === "agents") {
    const workers = data?.principals.filter((principal) =>
      principal.roles.includes("worker"),
    ).length;
    const openRuns = current?.attempts.filter((attempt) => attempt.state === "OPEN").length;
    return [
      {
        label: "Installed tools",
        value: installedTools ?? "—",
        note: installedTools === undefined ? "Readiness not available" : "Observed on this node",
      },
      {
        label: "Linked tools",
        value: linkedTools ?? "—",
        note:
          linkedTools === undefined
            ? "Readiness not available"
            : "MCP configured; session unverified",
      },
      { label: "Worker identities", value: workers ?? "—", note: "Canonical principals" },
      { label: "Open runs", value: openRuns ?? "—", note: "Attempts not submitted" },
    ];
  }

  if (section === "context") {
    const obligations = current?.contracts.reduce(
      (sum, contract) => sum + contract.content.obligations.length,
      0,
    );
    const sources = current
      ? new Set(
          current.contracts.flatMap((contract) =>
            contract.content.sources.map((source) => source.uri),
          ),
        ).size
      : undefined;
    return [
      {
        label: "Current contracts",
        value: current?.contracts.length ?? "—",
        note: "Completion-aware versions",
      },
      { label: "Current checks", value: obligations ?? "—", note: "Bound obligations" },
      { label: "Current sources", value: sources ?? "—", note: "Bound source records" },
      {
        label: "Current receipts",
        value:
          current?.receipts.filter((receipt) => receipt.payload.assurance === "PROSPECTIVE")
            .length ?? "—",
        note: "Prospective signed verdicts",
      },
    ];
  }

  const openWork = current?.work.filter((work) => work.state !== "CLOSED").length;
  const openReviews = current?.reviews.filter((review) => review.resolution === null).length;
  return [
    { label: "Current Work not closed", value: openWork ?? "—", note: "All states except closed" },
    { label: "Current runs", value: current?.attempts.length ?? "—", note: "Worker attempts" },
    { label: "Current open reviews", value: openReviews ?? "—", note: "Awaiting resolution" },
    {
      label: "Recorded effects",
      value: data?.effects.length ?? "—",
      note: "Unattributed delivery log, not completion authority",
    },
  ];
}

function sectionDistribution(section: WorkSectionId, data: UniversalConsoleOverview | undefined) {
  if (!data) return [];
  const current = projectUniversalCompletionOverview(data).current;
  if (section === "agents") {
    return ["OPEN", "SUBMITTED", "SUPERSEDED", "ABANDONED"].map((state) => ({
      label: readableDetailLabel(state),
      value: current.attempts.filter((attempt) => attempt.state === state).length,
    }));
  }
  if (section === "context") {
    return ["REPLAY", "SHADOW", "REVIEW_GATED", "ENFORCED", "PAUSED"].map((mode) => ({
      label: readableDetailLabel(mode),
      value: current.contracts.filter((contract) => contract.deploymentMode === mode).length,
    }));
  }
  return [
    { label: "Requested", states: ["REQUESTED", "CONTRACT_PENDING", "OPEN"] },
    { label: "Working", states: ["IN_PROGRESS"] },
    { label: "Submitted", states: ["SUBMITTED", "VERIFYING"] },
    { label: "Determined", states: ["DETERMINED", "ENFORCED"] },
    { label: "Reviewing", states: ["REVIEWING"] },
    { label: "Closed", states: ["CLOSED"] },
  ].map((stage) => ({
    label: stage.label,
    value: current.work.filter((work) => stage.states.includes(work.state)).length,
  }));
}

function SectionDistributionChart({
  section,
  data,
}: {
  section: WorkSectionId;
  data: UniversalConsoleOverview | undefined;
}) {
  const rows = sectionDistribution(section, data);
  const title =
    section === "agents"
      ? "Runs by state"
      : section === "context"
        ? "Contracts by mode"
        : "Work by stage";
  return (
    <figure className="vp-section-home-chart">
      <figcaption>{title}</figcaption>
      {rows.some((row) => row.value > 0) ? (
        <ResponsiveContainer width="100%" height={150} minWidth={0}>
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 2, right: 26, bottom: 2, left: 4 }}
            accessibilityLayer
          >
            <CartesianGrid stroke="#e5eae6" horizontal={false} />
            <XAxis
              type="number"
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#748078", fontSize: "var(--chart-label-size)" }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={76}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#536158", fontSize: "var(--chart-label-size)" }}
            />
            <Tooltip
              cursor={{ fill: "#f1f5f2" }}
              contentStyle={{
                ...CHART_TOOLTIP_STYLE,
                boxShadow: "none",
                fontSize: "var(--chart-label-size)",
              }}
            />
            <Bar dataKey="value" fill="#4f7f64" radius={[0, 3, 3, 0]} isAnimationActive={false}>
              <LabelList
                dataKey="value"
                position="right"
                fill="#536158"
                fontSize="var(--chart-label-size)"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p>No canonical records for this distribution.</p>
      )}
    </figure>
  );
}

function SectionHomePane({
  connection,
  section,
  pathname,
  onNavigate,
  onClose,
}: {
  connection: AuthenticationConnection;
  section: WorkSection;
  pathname: string;
  onNavigate: (to: string) => void;
  onClose: () => void;
}) {
  const verificationEnv = useVerificationEnvOptional();
  const canonicalEnvironment = useCanonicalEnvironmentOptional();
  const localEnvironmentReady = canonicalLocalEnvironmentAvailable(canonicalEnvironment);
  const universalOverview = useApi(
    universalVerification.overview,
    verificationEnv?.environment === "demo" || !localEnvironmentReady
      ? null
      : { query: { env: "real" } },
  );
  const agentReadiness = useApi(advisor.agentCliReadiness, {});
  const readyAgents = observedReadiness(agentReadiness, connection);
  const installedTools = readyAgents?.filter((cli) => cli.binary.installed).length;
  const linkedTools = readyAgents?.filter((cli) => cli.mcp.registered).length;
  const metrics = sectionMetrics(
    section.id,
    universalOverview.error ? undefined : universalOverview.data,
    installedTools,
    linkedTools,
  );
  const incompleteRecordCount = universalOverview.data
    ? Object.values(projectUniversalCompletionOverview(universalOverview.data).incomplete).reduce(
        (sum, records) => sum + records.length,
        0,
      )
    : 0;
  const universalState =
    !localEnvironmentReady
      ? "Canonical Work records are unavailable for this Environment. No local fallback values are substituted."
      : verificationEnv?.environment === "demo"
      ? "Canonical Work records are available in the active Environment."
      : universalOverview.error
        ? "Canonical Work records are unavailable. No fallback values are substituted."
        : universalOverview.data
          ? `Canonical ledger generated ${new Date(universalOverview.data.generatedAt).toLocaleString()}. ${incompleteRecordCount > 0 ? `${incompleteRecordCount} historical records remain readable as INCOMPLETE or STALE. Recorded statuses are not current authority.` : "All displayed verification chains are current."}`
          : "Reading the canonical Work ledger.";

  return (
    <section
      className="vp-companion vp-section-home-pane"
      data-pane="section-home"
      data-section-home={section.id}
      aria-labelledby="vp-section-home-title"
    >
      <header className="vp-pane-bar">
        <span className="vp-companion-mark" aria-hidden="true">
          {section.id === "agents" ? (
            <AgentMark agentKey="agents" size={16} />
          ) : (
            <Icon name={section.icon} size={14} />
          )}
        </span>
        <strong>{section.title}</strong>
        <span className="vp-pane-spacer" />
        <button
          type="button"
          className="vp-pane-icon"
          data-action-id="shell.close-section-home"
          onClick={onClose}
          aria-label={`Close ${section.title}`}
          title={`Close ${section.title}`}
        >
          <X size={15} />
        </button>
      </header>
      <div className="vp-section-home-scroll" id="vp-content" tabIndex={-1}>
        <header className="vp-section-home-intro">
          <span>Section home</span>
          <h1 id="vp-section-home-title" className="vp-section-home-title">
            {section.title}
          </h1>
          <p>{section.lede}</p>
        </header>
        <dl
          className="vp-section-home-metrics"
          aria-label={`${section.title} summary`}
          aria-busy={universalOverview.isLoading && !universalOverview.data}
        >
          {metrics.map((metric) => (
            <div key={metric.label}>
              <dt>{metric.label}</dt>
              <dd>{metric.value}</dd>
              <small>{metric.note}</small>
            </div>
          ))}
        </dl>
        <p className="vp-section-home-source" role={universalOverview.error ? "status" : undefined}>
          {universalState}
        </p>
        <SectionDistributionChart section={section.id} data={universalOverview.data} />
        <nav className="vp-section-home-items" aria-label={`${section.title} features`}>
          {section.items.map((item) => {
            const itemPath = splitAppHref(item.href).pathname;
            const active = pathMatchesPrefix(pathname, itemPath) && pathname !== section.root;
            return (
              <button
                key={item.href}
                type="button"
                data-action-id="shell.nav-item"
                data-active={active ? "true" : undefined}
                aria-current={active ? "page" : undefined}
                onClick={() => onNavigate(item.href)}
              >
                <span className="vp-section-home-icon">
                  <Icon name={item.icon} size={16} />
                </span>
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
                <Icon name="caret-right" size={9} />
              </button>
            );
          })}
        </nav>
      </div>
    </section>
  );
}

function HomeRootPane({ onNavigate }: { onNavigate: (to: string) => void }) {
  return (
    <section className="vp-companion vp-home-root-pane" data-pane="home" aria-label="Home">
      <div className="vp-home-root-scroll" id="vp-content" tabIndex={-1}>
        <div className="vp-companion-home" data-companion-home="recognition">
          <HomePage onNavigate={onNavigate} />
        </div>
      </div>
    </section>
  );
}

function relativeActivityTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "recent";
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

const AGENT_ACTIVITY_KINDS = new Set([
  "agent",
  "agent_session",
  "session",
  "task",
  "worker",
  "worker_run",
  "work_attempt",
]);

function WorkActivityMark({ groupKey }: { groupKey: string }) {
  const [entityKind = "", verb = ""] = groupKey.split(":", 2);
  if (
    AGENT_ACTIVITY_KINDS.has(entityKind) ||
    verb.startsWith("acp_") ||
    verb.startsWith("agent_") ||
    verb.startsWith("worker_")
  ) {
    return <AgentMark agentKey="agents" size={14} />;
  }

  let icon: IconName = "chart";
  if (entityKind === "branch" || entityKind === "commit" || entityKind === "repository")
    icon = "code";
  else if (["check", "gate", "verdict", "verification", "proof", "run"].includes(entityKind))
    icon = "check";
  else if (
    ["workflow", "compiled_workflow", "workflow_run", "loop", "automation"].includes(entityKind)
  )
    icon = "play";
  else if (["evidence", "receipt", "pack"].includes(entityKind)) icon = "seal";
  else if (entityKind === "control" || entityKind === "policy") icon = "shield";
  return <Icon name={icon} size={14} />;
}

function CompanionPane({
  route,
  pathname,
  connection,
  onRetryConnection,
  copilotEnabled,
  onNavigate,
}: {
  route: { page: PageId; id?: string };
  pathname: string;
  connection: AuthenticationConnection;
  onRetryConnection: () => Promise<unknown>;
  copilotEnabled: boolean;
  onNavigate: (to: string) => void;
}) {
  const [question, setQuestion] = useState("");
  const [compactThread, setCompactThread] = useState(false);
  const context = shellContext(pathname, route);
  const workTitle = workTitleFor(pathname, route);
  const agentReadiness = useApi(advisor.agentCliReadiness, {});
  const readyAgents = observedReadiness(agentReadiness, connection);
  const installedAgents = readyAgents?.filter((cli) => cli.binary.installed) ?? [];
  const linkedAgents = installedAgents.filter((cli) => cli.mcp.registered);
  const agentLabels = installedAgents.map((cli) => cli.label);
  const readinessUnavailable = connection === "unavailable" || Boolean(agentReadiness.error);
  const agentRecognition =
    connection === "sign-in"
      ? "Sign in to check this computer for agent tools."
      : readinessUnavailable
        ? "I could not check for local agent tools right now."
        : !readyAgents
          ? "I am checking this computer for agent tools."
          : agentLabels.length > 0
            ? `I found ${formatList(agentLabels)} on this computer.`
            : "I did not find a supported agent tool on this computer yet.";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = question.trim();
    if (!query) return;
    if (!copilotEnabled) {
      onNavigate(`/workflows/new?intent=${encodeURIComponent(query)}`);
      setQuestion("");
      return;
    }
    window.dispatchEvent(
      new CustomEvent("excellent:ask-agent", {
        detail: { query: `${query}\n\nCurrent Excellent context: ${pathname}` },
      }),
    );
    setQuestion("");
  }

  return (
    <aside
      className="vp-companion"
      aria-labelledby="vp-coordinator-title"
      data-compact-thread={compactThread ? "true" : undefined}
    >
      <header className="vp-pane-bar">
        <span className="vp-companion-mark">
          <Icon name="seal" size={14} />
        </span>
        <strong id="vp-coordinator-title">Coordinator</strong>
        <span className="vp-pane-spacer" />
        <button
          type="button"
          className="vp-pane-icon vp-pane-text"
          data-action-id="companion.toggle-compact-thread"
          onClick={() => setCompactThread((value) => !value)}
          aria-label="Toggle compact coordinator text"
          title="Text density"
        >
          Aa
        </button>
        <button
          type="button"
          className="vp-pane-icon"
          data-action-id="companion.copy-context"
          onClick={() => void navigator.clipboard?.writeText(`${workTitle}\n${context.purpose}`)}
          aria-label="Copy coordinator context"
          title="Copy context"
        >
          <Icon name="copy" size={14} />
        </button>
        <button
          type="button"
          className="vp-pane-icon"
          data-action-id="companion.clear-draft"
          onClick={() => setQuestion("")}
          aria-label="Clear coordinator draft"
          title="Clear draft"
        >
          <Trash2 size={14} />
        </button>
        <button
          type="button"
          className="vp-pane-icon"
          data-action-id="companion.close-work"
          onClick={() => onNavigate("/")}
          aria-label="Close current work"
          title="Close work"
        >
          <Icon name="x" size={14} />
        </button>
      </header>
      <div className="vp-companion-scroll">
        <div className="vp-thread-kicker">Current work</div>
        <p className="vp-thread-lead">{workTitle}</p>
        <p className="vp-thread-copy">{context.purpose}</p>

        <section
          className="vp-thread-block"
          data-companion-recognition="agent-presence"
          data-companion-recognition-state={
            connection === "sign-in" || readinessUnavailable
              ? "unavailable"
              : !readyAgents
                ? "checking"
                : installedAgents.length > 0
                  ? "observed"
                  : "empty"
          }
        >
          <div className="vp-thread-line">
            <span className="vp-companion-mark">
              <Icon name="seal" size={12} />
            </span>
            <strong>Excellent</strong>
            <span>{installedAgents.length > 0 ? "Observed locally" : "Local observation"}</span>
          </div>
          <p className="vp-observation-headline">{agentRecognition}</p>
          {installedAgents.length > 0 ? (
            <div className="vp-observed-tools" aria-label="Observed agent tools">
              {installedAgents.map((cli) => (
                <span key={cli.id} data-linked={cli.mcp.registered ? "true" : undefined}>
                  <span className="vp-live-dot" aria-hidden="true" />
                  {cli.label}
                  <small>
                    {cli.mcp.registered
                      ? "MCP configured; session unverified"
                      : "MCP not configured"}
                  </small>
                </span>
              ))}
            </div>
          ) : null}
          <p className="vp-observation-boundary">
            This check reads binary availability locally. Registration and session history require
            separate access. Nothing is sent elsewhere.
          </p>
          <div className="vp-thread-actions">
            <button
              type="button"
              className="vp-thread-primary"
              data-action-id="companion.review-agent-access"
              onClick={() => {
                if (connection === "sign-in") onNavigate("/login");
                else if (readinessUnavailable) {
                  void onRetryConnection();
                  void agentReadiness.mutate();
                } else onNavigate("/settings/agents/clis");
              }}
            >
              {connection === "sign-in"
                ? "Sign in"
                : readinessUnavailable
                  ? "Retry readiness check"
                  : readyAgents
                    ? installedAgents.length > 0
                      ? "Review access"
                      : "Connect an agent"
                    : "Review agent access"}
            </button>
            {linkedAgents.length > 0 ? (
              <button
                type="button"
                data-action-id="companion.open-agent-activity"
                onClick={() => onNavigate("/activity")}
              >
                See agent activity
              </button>
            ) : null}
          </div>
        </section>

        <section className="vp-thread-block">
          <div className="vp-thread-line">
            <span className="vp-companion-mark">
              <Icon name="seal" size={12} />
            </span>
            <strong>Coordinator</strong>
            <span>Next decision</span>
          </div>
          <p>
            I can inspect bounded aggregate Claude tool events locally after one grant, propose a
            candidate workflow, and leave execution for a separate confirmation.
          </p>
          <div className="vp-thread-actions">
            <button
              type="button"
              data-action-id="companion.open-evidence"
              onClick={() => onNavigate("/evidence")}
            >
              See evidence
            </button>
            <button
              type="button"
              data-action-id="companion.open-receipts"
              onClick={() => onNavigate("/receipts")}
            >
              Open receipts
            </button>
          </div>
        </section>

        <button
          className="vp-thread-artifact"
          type="button"
          data-action-id="companion.open-activity"
          onClick={() => onNavigate("/activity")}
        >
          <Icon name="chart" size={13} />
          <span>
            {readyAgents
              ? `${installedAgents.length} agent tool${installedAgents.length === 1 ? "" : "s"} observed on this node`
              : "Agent tool readiness is not available"}
          </span>
          <Icon name="caret-right" size={9} />
        </button>
        <div className="vp-thread-wait">
          <span>
            {connection === "sign-in"
              ? "Sign in to check agent access"
              : readinessUnavailable
                ? "Agent readiness could not be confirmed"
                : readyAgents
                  ? "Review agent access to continue"
                  : "Waiting for a readiness check"}
          </span>
        </div>
      </div>
      <form className="vp-companion-composer" onSubmit={submit}>
        <label htmlFor="vp-companion-question">Ask Excellent</label>
        <input
          id="vp-companion-question"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={copilotEnabled ? "Ask about this work" : "Describe what should be verified"}
          aria-describedby="vp-companion-mode"
        />
        <div className="vp-composer-tools">
          <span id="vp-companion-mode">{copilotEnabled ? "Local companion" : "Guided intake"}</span>
          <button
            type="submit"
            data-action-id="companion.send"
            onClick={() => undefined}
            aria-label={copilotEnabled ? "Send to Excellent" : "Continue to guided intake"}
          >
            <Icon name="arrow-right" size={14} />
          </button>
        </div>
      </form>
    </aside>
  );
}

function formatList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

function MobilePaneNav({
  active,
  onSelect,
  section,
  homeRoot,
  detailAvailable,
}: {
  active: "work" | "coordinator" | "details";
  onSelect: (pane: "work" | "coordinator" | "details") => void;
  section: WorkSection | null;
  homeRoot: boolean;
  detailAvailable: boolean;
}) {
  const panes = [
    { id: "work" as const, label: "Overview", icon: "chart" as const, available: true },
    {
      id: "coordinator" as const,
      label: homeRoot ? "Home" : (section?.title ?? "Coordinator"),
      icon: section?.icon ?? ("seal" as const),
      available: true,
    },
    {
      id: "details" as const,
      label: homeRoot || section ? "Details" : "Spec",
      icon: "book" as const,
      available: detailAvailable,
    },
  ];
  return (
    <nav className="vp-mobile-primary" aria-label="Panels">
      {panes.map((pane) => (
        <button
          key={pane.id}
          type="button"
          data-active={active === pane.id ? "true" : undefined}
          aria-pressed={active === pane.id}
          disabled={!pane.available}
          onClick={() => onSelect(pane.id)}
        >
          <Icon name={pane.icon} size={16} />
          <span>{pane.label}</span>
        </button>
      ))}
    </nav>
  );
}

function TopBar({
  route,
  onNavigate,
  onClose,
  titleOverride,
  railOpen,
  onToggleRail,
  copilotEnabled,
  pathname,
  topBarState,
}: {
  route: { page: PageId; id?: string };
  onNavigate: (to: string) => void;
  onClose?: () => void;
  titleOverride?: string;
  railOpen: boolean;
  onToggleRail: () => void;
  copilotEnabled: boolean;
  pathname: string;
  topBarState: VerificationTopBarState;
}) {
  const item = navItemFor(route.page);
  const isDetail = Boolean(route.id);
  const askKind = objectKindFromChrome(route.page, route.id);
  const paneTitle =
    titleOverride ??
    (pathname === "/"
      ? "Spec"
      : pathname === "/activity"
        ? "Activity"
        : route.page === "settings"
          ? "Settings"
          : (item?.label ?? "Details"));

  return (
    <div className="vp-topbar">
      <button
        type="button"
        className="vp-nav-toggle"
        data-action-id="shell.toggle-rail"
        onClick={onToggleRail}
        aria-expanded={railOpen}
        aria-controls="vp-context-navigation"
        aria-label={railOpen ? "Close navigation" : "Open navigation"}
      >
        <Icon name={railOpen ? "x" : "menu"} size={16} />
      </button>

      <div id="vp-detail-pane-title" className="vp-detail-pane-title">
        <Icon name="book" size={14} />
        <strong>{paneTitle}</strong>
      </div>
      <nav className="vp-crumbs" aria-label="Breadcrumb">
        {item && isDetail ? (
          <>
            <button
              type="button"
              className="vp-crumb"
              data-action-id="shell.crumb-page"
              onClick={() => onNavigate(pathOf(item))}
            >
              {item.label}
            </button>
            <span className="vp-crumb-sep" aria-hidden="true">
              /
            </span>
            <span className="vp-crumb-current">
              {topBarState.detailTitle ?? readableDetailLabel(route.id!)}
            </span>
          </>
        ) : null}
      </nav>

      <div className="vp-topbar-tools">
        <div id="vp-topbar-actions" className="vp-topbar-actions-slot" />
        <SearchButton />
        {askKind && route.id ? (
          <AskAboutObject
            kind={askKind}
            id={route.id}
            href={pathname}
            copilotEnabled={copilotEnabled}
            onNavigate={onNavigate}
          />
        ) : null}
        <button
          type="button"
          className="vp-pane-icon vp-detail-close"
          data-action-id="shell.close-detail"
          aria-label={`Close ${paneTitle}`}
          title={`Close ${paneTitle}`}
          onClick={onClose ?? (() => onNavigate("/"))}
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}

function readableDetailLabel(id: string): string {
  const decoded = decodeURIComponent(id);
  const stripped = decoded.replace(/^(demo|real):/i, "");
  const numeric = stripped.match(/(\d{3,})$/);
  if (numeric) return `Item #${numeric[1]}`;
  return stripped
    .replace(/^[a-z]+_/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
